import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TextInput,
  TouchableOpacity,
  ScrollView
} from 'react-native';
import { useAppTheme } from '../../utils/useAppTheme';

const CustomCipherModal = ({ visible, onClose, onSave, editingCipher = null, isHebrewMode = false }) => {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [letterValues, setLetterValues] = useState({});

  // English alphabet
  const englishAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  
  // Hebrew alphabet (22 regular letters + 5 final forms)
  const hebrewAlphabet = [
    'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י',
    'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ', 'ק', 'ר', 'ש', 'ת',
    'ך', 'ם', 'ן', 'ף', 'ץ' // Final forms
  ];
  
  const alphabet = isHebrewMode ? hebrewAlphabet : englishAlphabet;

  useEffect(() => {
    if (editingCipher) {
      setName(editingCipher.name);
      setDescription(editingCipher.description || '');
      setLetterValues(editingCipher.values || {});
    } else {
      // Initialize with default values
      const defaultValues = {};
      if (isHebrewMode) {
        // Hebrew default values (standard gematria)
        const hebrewDefaults = {
          'א': 1, 'ב': 2, 'ג': 3, 'ד': 4, 'ה': 5, 'ו': 6, 'ז': 7, 'ח': 8, 'ט': 9, 'י': 10,
          'כ': 20, 'ל': 30, 'מ': 40, 'נ': 50, 'ס': 60, 'ע': 70, 'פ': 80, 'צ': 90,
          'ק': 100, 'ר': 200, 'ש': 300, 'ת': 400,
          'ך': 500, 'ם': 600, 'ן': 700, 'ף': 800, 'ץ': 900
        };
        Object.assign(defaultValues, hebrewDefaults);
      } else {
        // English default values (A=1, B=2, etc.)
        alphabet.forEach((letter, index) => {
          defaultValues[letter] = index + 1;
          defaultValues[letter.toLowerCase()] = index + 1;
        });
      }
      setLetterValues(defaultValues);
    }
  }, [editingCipher, visible, isHebrewMode]);

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a cipher name');
      return;
    }

    const cipherData = {
      name: name.trim(),
      description: description.trim(),
      values: letterValues,
      category: 'Custom',
      isHebrewCipher: isHebrewMode
    };

    onSave(cipherData);
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setLetterValues({});
    onClose();
  };

  const updateLetterValue = (letter, value) => {
    const numValue = parseInt(value) || 0;
    setLetterValues({
      ...letterValues,
      [letter]: numValue,
      [letter.toLowerCase()]: numValue
    });
  };

  if (!visible) return null;

  return (
    <View style={styles.modalOverlay}>
      <View style={styles.modalContent}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            {editingCipher ? 'Edit Custom Cipher' : 'Create Custom Cipher'}
          </Text>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody}>
          <Text style={styles.label}>Cipher Name *</Text>
          <TextInput
            style={styles.input}
            placeholder="e.g., My Custom Cipher"
            placeholderTextColor={colors.lightText}
            value={name}
            onChangeText={setName}
          />

          <Text style={styles.label}>Description (optional)</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder="Describe your cipher..."
            placeholderTextColor={colors.lightText}
            value={description}
            onChangeText={setDescription}
            multiline
            numberOfLines={3}
          />

          <Text style={styles.label}>Letter Values</Text>
          <Text style={styles.hint}>
            {isHebrewMode 
              ? 'Assign a number to each Hebrew letter (א-ת + finals)'
              : 'Assign a number to each letter (A-Z)'}
          </Text>

          <View style={styles.letterGrid}>
            {alphabet.map((letter) => (
              <View key={letter} style={styles.letterItem}>
                <Text style={styles.letterLabel}>{letter}</Text>
                <TextInput
                  style={styles.letterInput}
                  value={letterValues[letter]?.toString() || '0'}
                  onChangeText={(value) => updateLetterValue(letter, value)}
                  onFocus={(e) => e.target.select()}
                />
              </View>
            ))}
          </View>
        </ScrollView>

        <View style={styles.modalFooter}>
          <TouchableOpacity
            style={[styles.button, styles.cancelButton]}
            onPress={handleClose}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.saveButton]}
            onPress={handleSave}
          >
            <Text style={styles.saveButtonText}>
              {editingCipher ? 'Update' : 'Create'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const createStyles = (colors) => StyleSheet.create({
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: colors.surfaceElevated,
    borderRadius: 8,
    width: '90%',
    maxWidth: 800,
    maxHeight: '90%',
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
    backgroundColor: colors.surfaceElevated,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
  },
  closeButton: {
    padding: 5,
  },
  closeButtonText: {
    fontSize: 24,
    color: colors.textSecondary,
  },
  modalBody: {
    padding: 20,
    maxHeight: 500,
    backgroundColor: colors.surface,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 5,
    marginTop: 15,
  },
  hint: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: 10,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    padding: 10,
    fontSize: 14,
    color: colors.text,
    backgroundColor: colors.inputBackground,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  letterGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 10,
  },
  letterItem: {
    width: '18%',
    minWidth: 60,
    alignItems: 'center',
    marginBottom: 10,
  },
  letterLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 5,
  },
  letterInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    padding: 5,
    fontSize: 12,
    color: colors.text,
    backgroundColor: colors.inputBackground,
    textAlign: 'center',
    width: '100%',
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
    backgroundColor: colors.surfaceElevated,
    gap: 10,
  },
  button: {
    flex: 1,
    padding: 12,
    borderRadius: 5,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: colors.mutedSurface,
  },
  cancelButtonText: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '600',
  },
  saveButton: {
    backgroundColor: colors.primary,
  },
  saveButtonText: {
    color: colors.onPrimary,
    fontSize: 14,
    fontWeight: '600',
  },
});

export default CustomCipherModal;
